/**
 * Encounter API client (T30 — real backend wired).
 *
 * The backend owns the authoritative 6-state machine
 * (OPEN/IN_PROGRESS/COMPLETED/SIGNED/AMENDED/VOIDED) and projects it down to
 * the 3 values this contract uses (IN_PROGRESS | SIGNED | CANCELLED). The UI
 * never drives a state directly — it calls semantic endpoints (sign/cancel)
 * and the server applies the legal transition. That is the FE↔machine
 * alignment; no UI rewrite was needed.
 *
 * Flip mock↔real with `VITE_USE_MOCKS` / `mock/registry.ts`.
 */
import { apiClient } from "./client";
import { MOCKS_ENABLED } from "./mock/registry";
import { mockEncounters } from "./mock/encounters";
import { mockIcd10 } from "./mock/icd10";
import type {
  Diagnosis,
  EncounterAttachment,
  EncounterDetail,
  EncounterExamMode,
  EncounterListFilter,
  EncounterPrescriptionItem,
  EncounterSummary,
  Icd10Entry,
  PresignUploadResponse,
  SoapNote,
  Vitals,
} from "../types/encounter";
import type { PageResponse } from "../types/patient";

const realClient = {
  list(
    filter: EncounterListFilter | undefined,
    pageable: { page?: number; size?: number; sort?: string }
  ): Promise<PageResponse<EncounterSummary>> {
    const params: Record<string, string | number> = {};
    if (filter?.q) params.q = filter.q;
    if (filter?.status) params.status = filter.status;
    if (filter?.doctorId) params.doctorId = filter.doctorId;
    if (filter?.patientId) params.patientId = filter.patientId;
    if (filter?.fromDate) params.fromDate = filter.fromDate;
    if (filter?.toDate) params.toDate = filter.toDate;
    if (pageable.page != null) params.page = pageable.page;
    if (pageable.size != null) params.size = pageable.size;
    return apiClient
      .get<PageResponse<EncounterSummary>>("/v1/encounters", { params })
      .then((r) => r.data);
  },

  /** @param roomId narrow to one of YOUR rooms; omitted means all of them (#18882). */
  queue(roomId?: string | null): Promise<EncounterSummary[]> {
    return apiClient
      .get<EncounterSummary[]>("/v1/encounters/queue", {
        params: roomId ? { roomId } : {},
      })
      .then((r) => r.data);
  },

  get(encounterId: string): Promise<EncounterDetail> {
    return apiClient
      .get<EncounterDetail>(`/v1/encounters/${encounterId}`)
      .then((r) => r.data);
  },

  createFromAppointment(input: {
    appointmentId: string;
    patientId: string;
    patientName: string;
    patientCode: string | null;
    patientPhone: string | null;
    patientGender: "M" | "F" | "OTHER" | "UNKNOWN" | null;
    patientAge: number | null;
    // #18899 — nullable because an appointment with no doctor is a supported state
    // ("Bác sĩ: Ẩn · Lễ tân xếp"). It was typed non-null, so callers reached for `?? ""`
    // to satisfy it; the server then coerced "" to null and reported a null it had never
    // been sent. Typing the absence honestly keeps the two ends describing one value.
    doctorId: string | null;
    doctorName: string | null;
    chiefComplaint?: string | null;
  }): Promise<EncounterDetail> {
    return apiClient
      .post<EncounterDetail>("/v1/encounters", {
        appointmentId: input.appointmentId,
        patientId: input.patientId,
        doctorId: input.doctorId,
        chiefComplaint: input.chiefComplaint ?? null,
      })
      .then((r) => r.data);
  },

  getExamMode(encounterId: string): Promise<EncounterExamMode> {
    return apiClient
      .get<EncounterExamMode>(`/v1/encounters/${encounterId}/exam-mode`)
      .then((r) => r.data);
  },
  updateExamMode(
    encounterId: string,
    req: { examType: "SERVICE" | "BHYT"; bhytCardNo: string | null },
  ): Promise<EncounterExamMode> {
    return apiClient
      .patch<EncounterExamMode>(`/v1/encounters/${encounterId}/exam-mode`, req)
      .then((r) => r.data);
  },
  updateVitals(encounterId: string, v: Vitals): Promise<void> {
    return apiClient
      .patch<void>(`/v1/encounters/${encounterId}/vitals`, {
        systolicBp: v.systolicBp,
        diastolicBp: v.diastolicBp,
        heartRate: v.heartRate,
        temperatureC: v.temperatureC,
        respiratoryRate: v.respiratoryRate,
        spO2: v.spO2,
        weightKg: v.weightKg,
        heightCm: v.heightCm,
      })
      .then(() => undefined);
  },

  /**
   * @param amendmentReason required by the API only when this overwrites a SIGNED note — the API
   *   rejects such a save without one (TT 13/2025/TT-BYT, #18727). Editing an unsigned draft needs
   *   nothing, so this stays optional rather than making every autosave carry a ceremony.
   */
  updateSoap(
    encounterId: string,
    soap: Omit<SoapNote, "updatedAt">,
    amendmentReason?: string
  ): Promise<void> {
    return apiClient
      .patch<void>(`/v1/encounters/${encounterId}/soap`, soap, {
        params: amendmentReason ? { amendmentReason } : undefined,
      })
      .then(() => undefined);
  },

  updateChiefComplaint(
    encounterId: string,
    chiefComplaint: string | null
  ): Promise<void> {
    return apiClient
      .patch<void>(`/v1/encounters/${encounterId}/chief-complaint`, {
        chiefComplaint,
      })
      .then(() => undefined);
  },

  replaceDiagnoses(
    encounterId: string,
    diagnoses: Omit<Diagnosis, "id">[]
  ): Promise<void> {
    return apiClient
      .put<void>(`/v1/encounters/${encounterId}/diagnoses`, diagnoses)
      .then(() => undefined);
  },

  replacePrescription(
    encounterId: string,
    items: Omit<EncounterPrescriptionItem, "id">[]
  ): Promise<void> {
    return apiClient
      .put<void>(`/v1/encounters/${encounterId}/prescription`, items)
      .then(() => undefined);
  },

  /**
   * Replaces the encounter's paraclinical orders as DATA (#18805).
   *
   * <p>This is the only place orders are written since #18839. They used to be dual-written into
   * `soap.assessment.supplementaryLabUs` as "Chỉ định: a; b" — a field whose own label is "Kết quả
   * XN, siêu âm bổ sung" and which the notes screen edits as free text, so the two screens
   * silently erased each other.
   */
  /** The encounter's paraclinical orders as DATA (#18805/#18839). */
  listServiceOrders(
    encounterId: string,
  ): Promise<{ id: string; serviceId: string | null; serviceName: string; note: string | null }[]> {
    return apiClient
      .get<{ id: string; serviceId: string | null; serviceName: string; note: string | null }[]>(
        `/v1/encounters/${encounterId}/service-orders`,
      )
      .then((r) => r.data);
  },

  replaceServiceOrders(
    encounterId: string,
    orders: { serviceId: string | null; serviceName: string; note?: string | null }[],
  ): Promise<unknown> {
    return apiClient
      .put(`/v1/encounters/${encounterId}/service-orders`, { orders })
      .then((r) => r.data);
  },
  signEncounter(encounterId: string): Promise<void> {
    return apiClient
      .post<void>(`/v1/encounters/${encounterId}/sign`)
      .then(() => undefined);
  },

  /**
   * Tạo lịch tái khám (follow-up appointment) từ lần khám. Frozen contract:
   * body `{ date }` → appointment + phiếu thu phí khám (có thể chưa thanh toán).
   */
  createFollowUp(
    id: string,
    date: string,
    dims?: {
      departmentId?: string | null;
      roomId?: string | null;
      doctorId?: string | null;
      serviceId?: string | null;
    }
  ): Promise<{
    appointmentId: string;
    date: string;
    examFeeAmount: number;
    examFeeStatus: string;
  }> {
    return apiClient
      .post<{
        appointmentId: string;
        date: string;
        examFeeAmount: number;
        examFeeStatus: string;
      }>(`/v1/encounters/${id}/follow-up`, {
        date,
        departmentId: dims?.departmentId ?? null,
        roomId: dims?.roomId ?? null,
        doctorId: dims?.doctorId ?? null,
        serviceId: dims?.serviceId ?? null,
      })
      .then((r) => r.data);
  },

  /**
   * Chuyển khoa — route the (signed) encounter's patient to another khoa's waiting
   * queue. Frozen contract: body `{ departmentId, doctorProfileId?, roomId?, reason? }`
   * → new CHECKED_IN appointment in the target khoa + its phí khám. 422 if the current
   * encounter is not signed yet or the target khoa is unknown/inactive.
   */
  transferDepartment(
    id: string,
    input: {
      departmentId: string;
      doctorProfileId?: string | null;
      roomId?: string | null;
      reason?: string | null;
    }
  ): Promise<{
    appointmentId: string;
    departmentId: string;
    departmentName: string;
    examFeeAmount: number;
    examFeeStatus: string;
  }> {
    return apiClient
      .post<{
        appointmentId: string;
        departmentId: string;
        departmentName: string;
        examFeeAmount: number;
        examFeeStatus: string;
      }>(`/v1/encounters/${id}/transfer`, {
        departmentId: input.departmentId,
        doctorProfileId: input.doctorProfileId ?? null,
        roomId: input.roomId ?? null,
        reason: input.reason ?? null,
      })
      .then((r) => r.data);
  },

  cancelEncounter(encounterId: string, reason: string): Promise<void> {
    return apiClient
      .post<void>(`/v1/encounters/${encounterId}/cancel`, { reason })
      .then(() => undefined);
  },

  /**
   * Hard-delete an UNSIGNED encounter permanently — for clearing demo/test data. Cascades, 204.
   *
   * <p>Signed encounters are refused (409 `encounter_signed_cannot_delete`) and blocked by a
   * database trigger regardless of the path (#18725, Thông tư 13/2025/TT-BYT). Needs the
   * `encounter.purge` permission, which only `clinic_owner` holds — not every doctor, as before.
   *
   * @param reason required by the API; an irreversible delete with no stated cause is what an audit
   *   cannot accept.
   */
  remove(encounterId: string, reason: string): Promise<void> {
    return apiClient
      .delete(`/v1/encounters/${encounterId}/permanent`, { params: { reason } })
      .then(() => undefined);
  },

  /**
   * Real binary upload (M11 object storage), 3 steps the browser drives:
   *   1) presign  → server creates the PENDING row + returns a presigned PUT
   *   2) PUT       → bytes go straight to object storage (not through the API)
   *   3) complete  → server stat()s the object, flips the row to READY
   * The file never transits the API server.
   */
  async uploadAttachment(
    encounterId: string,
    file: File,
    category: EncounterAttachment["category"]
  ): Promise<EncounterAttachment> {
    const ticket = await apiClient
      .post<PresignUploadResponse>(
        `/v1/encounters/${encounterId}/attachments/presign`,
        {
          fileName: file.name,
          mimeType: file.type || "application/octet-stream",
          sizeBytes: file.size,
          category,
        }
      )
      .then((r) => r.data);

    const put = await fetch(ticket.uploadUrl, {
      method: ticket.httpMethod || "PUT",
      body: file,
      headers: { "Content-Type": file.type || "application/octet-stream" },
    });
    if (!put.ok) {
      throw new Error(`Tải tệp lên kho lưu trữ thất bại (HTTP ${put.status}).`);
    }

    return apiClient
      .post<EncounterAttachment>(
        `/v1/encounters/${encounterId}/attachments/${ticket.attachmentId}/complete`
      )
      .then((r) => r.data);
  },

  /**
   * Bật/tắt "cho bệnh nhân xem" trên một tệp (#18735).
   *
   * <p>Closed by default: the clinic opens a file deliberately, one at a time. Which images are
   * safe for a patient to see alone is a clinical judgement, not something the software infers.
   */
  setAttachmentPatientVisible(
    encounterId: string,
    attachmentId: string,
    visible: boolean
  ): Promise<void> {
    return apiClient
      .put<void>(
        `/v1/encounters/${encounterId}/attachments/${attachmentId}/patient-visible`,
        null,
        { params: { visible } }
      )
      .then(() => undefined);
  },

  /**
   * Authenticated download. The API 302-redirects to a short-lived presigned
   * GET; the browser follows it (the cross-origin redirect drops the bearer,
   * which the presigned URL does not need) and we save the returned blob.
   */
  async downloadAttachment(
    encounterId: string,
    attachmentId: string,
    fileName: string
  ): Promise<void> {
    const blob = await apiClient
      .get<Blob>(
        `/v1/encounters/${encounterId}/attachments/${attachmentId}/download`,
        { responseType: "blob" }
      )
      .then((r) => r.data);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },

  removeAttachment(
    encounterId: string,
    attachmentId: string
  ): Promise<void> {
    return apiClient
      .delete<void>(`/v1/encounters/${encounterId}/attachments/${attachmentId}`)
      .then(() => undefined);
  },

  /** Thu hồi liên kết công khai xem phiếu (QR trên phiếu in) — #19150. */
  revokeShareLink(encounterId: string): Promise<void> {
    return apiClient
      .post<void>(`/v1/encounters/${encounterId}/share-link/revoke`)
      .then(() => undefined);
  },

  /** Cấp liên kết công khai MỚI (hạn 7 ngày); liên kết cũ chết ngay — #19150. */
  renewShareLink(encounterId: string): Promise<ShareLink> {
    return apiClient
      .post<ShareLink>(`/v1/encounters/${encounterId}/share-link/renew`)
      .then((r) => r.data);
  },
};

export interface ShareLink {
  publicToken: string;
  orderCode: string;
  expiresAt: string;
}

const realIcd10 = {
  search(q: string, limit = 12): Promise<Icd10Entry[]> {
    return apiClient
      .get<Icd10Entry[]>("/v1/icd10", { params: { q, limit } })
      .then((r) => r.data);
  },
  getByCode(code: string): Promise<Icd10Entry | null> {
    return apiClient
      .get<Icd10Entry>(`/v1/icd10/${encodeURIComponent(code)}`)
      .then((r) => r.data)
      .catch(() => null);
  },
};

export const encountersApi = MOCKS_ENABLED ? mockEncounters : realClient;
export const icd10Api = MOCKS_ENABLED ? mockIcd10 : realIcd10;

export const encounterQueryKeys = {
  all: ["encounters"] as const,
  list: (filter: unknown, page: number) =>
    ["encounters", "list", filter, page] as const,
  /** Prefix key — invalidating this still matches every per-room key below. */
  queue: () => ["encounters", "queue"] as const,
  /** Per-room, so switching rooms refetches instead of showing the previous room's list. */
  queueRoom: (roomId?: string | null) =>
    ["encounters", "queue", roomId ?? "all"] as const,
  detail: (id: string) => ["encounters", "detail", id] as const,
  serviceOrders: (id: string) => ["encounters", "service-orders", id] as const,
};
